import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { uploadArtwork } from "../lib/api";
import { ArtworkUpload } from "./ArtworkUpload";
import { FileCheck2, UploadCloud, X, Check } from "lucide-react";

const EXTS = ["pdf", "png", "jpg", "jpeg", "svg", "ai", "eps"];

export function MultiArtwork({ kinds, art, setArt }) {
  const setMode = (mode) => setArt((a) => ({ ...a, mode }));
  return (
    <div className="space-y-4" data-testid="custom-multi-artwork">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {[["combined", "One combined file", `A single file containing all ${kinds} designs, clearly labelled.`], ["separate", "Separate file per design", `Upload ${kinds} files — one for each design.`]].map(([id, title, sub]) => (
          <button key={id} onClick={() => setMode(id)} aria-pressed={art.mode === id} data-testid={`custom-art-mode-${id}`}
            className={`rounded-2xl border-2 p-4 text-left transition-colors ${art.mode === id ? "border-[color:var(--brand)] bg-[color:var(--brand-soft)]" : "border-[#E2DDD5] bg-white hover:border-[color:var(--brand)]"}`}>
            <div className="font-display font-bold">{title}</div>
            <div className="text-xs text-slate-500 mt-1">{sub}</div>
          </button>
        ))}
      </div>
      {art.mode === "combined" ? (
        <>
          <ArtworkUpload file={art.file} setFile={(f) => setArt((a) => ({ ...a, file: f }))} testPrefix="custom" />
          <label className="flex items-start gap-3 text-sm cursor-pointer" data-testid="custom-art-confirm">
            <input type="checkbox" className="mt-1" checked={art.confirmed} onChange={(e) => setArt((a) => ({ ...a, confirmed: e.target.checked }))} data-testid="custom-art-confirm-checkbox" />
            <span>My file contains all <strong>{kinds}</strong> designs, each clearly labelled.</span>
          </label>
        </>
      ) : (
        <div className="space-y-2" data-testid="custom-art-separate-list">
          {Array.from({ length: kinds }, (_, i) => (
            <DesignFileRow key={i} index={i} file={art.files[i]} onChange={(f) => setArt((a) => {
              const files = [...a.files];
              files[i] = f;
              return { ...a, files };
            })} />
          ))}
        </div>
      )}
    </div>
  );
}

function DesignFileRow({ index, file, onChange }) {
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const pick = async (f) => {
    if (!f) return;
    const ext = f.name.split(".").pop()?.toLowerCase();
    if (!EXTS.includes(ext)) { toast.error("Please upload PDF, PNG, JPG, SVG, AI or EPS"); return; }
    if (f.size > 50 * 1024 * 1024) { toast.error("Max 50 MB"); return; }
    setBusy(true);
    try {
      const res = await uploadArtwork(f);
      onChange({ ...res, name: f.name, design: `Design ${index + 1}` });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex items-center gap-3 rounded-xl border border-[#E2DDD5] bg-white px-4 py-3" data-testid={`custom-design-row-${index + 1}`}>
      <span className="font-display font-bold text-sm w-20 flex-shrink-0">Design {index + 1}</span>
      <input ref={ref} type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.svg,.ai,.eps" onChange={(e) => pick(e.target.files?.[0])} data-testid={`custom-design-input-${index + 1}`} />
      {file ? (
        <>
          <FileCheck2 size={18} className="text-[#10B981] flex-shrink-0" />
          <span className="text-sm truncate flex-1">{file.name}</span>
          <Check size={14} className="text-[#10B981]" />
          <button onClick={() => onChange(undefined)} className="btn-ghost !p-1" aria-label={`Remove design ${index + 1}`} data-testid={`custom-design-remove-${index + 1}`}><X size={16} /></button>
        </>
      ) : (
        <button onClick={() => ref.current?.click()} className="ml-auto inline-flex items-center gap-1.5 text-sm font-semibold text-[color:var(--brand)]" data-testid={`custom-design-upload-${index + 1}`}>
          <UploadCloud size={16} /> {busy ? "Uploading…" : "Upload file"}
        </button>
      )}
    </div>
  );
}
